/**
 * SCRUM-1032 — what the Configuration cell shows.
 *
 * Two server fields: `configuration`, the name of `configurationId`, and
 * `configState`, the line unit's config state.
 *
 * Round 2 treated `configState` as unreliable and overrode it in two places —
 * "configured" with an Unknown configuration was shown as unconfigured, and an
 * "autoswitched" state with a real configuration was shown as configured — on
 * the reasoning that the backend never resets the state. Reading the backend
 * (28 Sep) says that is the wrong conclusion:
 *
 * - `ControllerAppService.ControlAsync` writes the new state in the same
 *   request as the device call, and pushes the same `cfgState` to SRC.
 * - `SyncDataJob.SyncDataIO`, run from the object sync every 20 seconds
 *   (`Hangfire:SyncObjectData:Cron`), rebuilds each decoder row from SRC and
 *   keeps only local-only fields (purpose, ordinal, switch params). SRC's
 *   `cfgState` wins on every poll.
 *
 * So our copy is not a stale local guess: it is what SRC holds, which is what
 * the controller's own page shows. Overriding it put us in disagreement with
 * crysberg over the same decoder — "crysberg showing configured, but in
 * cisgenx showing unconfigured" (Wei Woon, 27 Sep). The state is sticky, but it
 * is sticky on both sides, and that is the device's business to report, not
 * this screen's to second-guess.
 *
 * What is left is a naming nicety, and the one QC actually asked for: an
 * autoswitched decoder has configuration id 0, whose name is "Unknown", so the
 * cell reads "Autoswitch" rather than "Unknown" (round 1, 14 Sep).
 */

export type DecoderConfigState = 'configured' | 'unconfigured' | 'autoswitched' | 'other'

export interface DecoderConfigDisplay {
  /** 'autoswitch' → show the Autoswitch label instead of the config name. */
  label: 'name' | 'autoswitch'
  /** The server's state, as the server gives it. */
  state: DecoderConfigState
  /** The state word itself, for anything outside the three known ones. */
  rawState: string
}

const stateOf = (raw: string): DecoderConfigState => {
  const key = raw.trim().toLowerCase().replace(/[\s_-]+/g, '')
  if (key === 'configured') return 'configured'
  if (key === 'unconfigured') return 'unconfigured'
  if (key.startsWith('autoswitch')) return 'autoswitched'
  return 'other'
}

const isUnknownConfig = (row: any) => {
  const id = Number(row?.configurationId)
  if (Number.isFinite(id)) return id === 0
  return String(row?.configuration ?? '').trim().toLowerCase() === 'unknown'
}

export const decoderConfigDisplay = (row: any): DecoderConfigDisplay => {
  const rawState = String(row?.configState ?? '').trim()
  const state = stateOf(rawState)

  return {
    // Only when there is no configuration to name: with one applied, the
    // configuration's own name is the more useful of the two.
    label:
      state === 'autoswitched' && isUnknownConfig(row) ? 'autoswitch' : 'name',
    state,
    rawState,
  }
}
